import React, { useContext, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import TicketsLabelSelect from "../TicketsLabelSelect";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: theme.palette.cards.background,
    color: theme.palette.text.primary,
  },
  listHeader: {
    flex: "none",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    padding: theme.spacing(2, 2, 1.5),
    borderBottom: theme.border.divider,
  },
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  listTitle: {
    fontWeight: 700,
    fontSize: "1.0625rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  listCount: {
    flex: "none",
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
  },
  searchWrapper: {
    display: "flex",
    alignItems: "center",
    height: 40,
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.borderRadius.input,
    border: theme.border.divider,
    backgroundColor: theme.palette.background.default,
  },
  searchIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  searchInput: {
    flex: 1,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 34,
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.borderRadius.pill,
    border: theme.border.divider,
    backgroundColor: theme.palette.cards.background,
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  filterChipSelected: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  filterCount: {
    minWidth: 18,
    padding: "1px 6px",
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.palette.action.selected,
    fontSize: "0.6875rem",
    textAlign: "center",
  },
  filterCountSelected: {
    backgroundColor: "rgba(255, 255, 255, 0.25)",
  },
  listBody: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
}));

const QUICK_FILTERS = ["all", "waiting", "hot"];

const TicketsManager = ({ selectedWhatsappId, advisorName, summary = [] }) => {
  const classes = useStyles();
  const [searchParam, setSearchParam] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const searchTimeout = useRef();
  const { user } = useContext(AuthContext);
  const selectedQueueIds = useMemo(() => user.queues.map((q) => q.id), [user]);

  const selectedWhatsappIds = useMemo(
    () => (selectedWhatsappId ? [selectedWhatsappId] : []),
    [selectedWhatsappId]
  );

  const listLabels = useMemo(
    () =>
      quickFilter === "hot" && !selectedLabels.includes("HOT")
        ? [...selectedLabels, "HOT"]
        : selectedLabels,
    [quickFilter, selectedLabels]
  );

  const counts = useMemo(() => {
    const rows = selectedWhatsappId
      ? summary.filter((s) => s.whatsappId === selectedWhatsappId)
      : summary;
    return rows.reduce(
      (acc, s) => ({
        all: acc.all + s.active,
        waiting: acc.waiting + s.waiting,
        hot: acc.hot + s.hot,
      }),
      { all: 0, waiting: 0, hot: 0 }
    );
  }, [summary, selectedWhatsappId]);

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase().trim();

    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <div className={classes.listHeader}>
        <div className={classes.titleRow}>
          <Typography component="h2" className={classes.listTitle}>
            {i18n.t("advisors.chatsOf", { name: advisorName })}
          </Typography>
          <span className={classes.listCount}>
            {i18n.t("advisors.chatCount", { count: counts.all })}
          </span>
        </div>
        <label className={classes.searchWrapper}>
          <SearchIcon fontSize="small" className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder={i18n.t("advisors.searchPlaceholder")}
            inputProps={{ "aria-label": i18n.t("advisors.searchPlaceholder") }}
            type="search"
            onChange={handleSearch}
          />
        </label>
        {!searchParam && (
          <div
            className={classes.filters}
            role="group"
            aria-label={i18n.t("advisors.filters.label")}
          >
            {QUICK_FILTERS.map((filter) => {
              const selected = quickFilter === filter;
              return (
                <ButtonBase
                  key={filter}
                  component="button"
                  focusRipple
                  aria-pressed={selected}
                  onClick={() => setQuickFilter(filter)}
                  className={clsx(
                    classes.filterChip,
                    selected && classes.filterChipSelected
                  )}
                >
                  {i18n.t(`advisors.filters.${filter}`)}
                  <span
                    className={clsx(
                      classes.filterCount,
                      selected && classes.filterCountSelected
                    )}
                  >
                    {counts[filter]}
                  </span>
                </ButtonBase>
              );
            })}
            <TicketsLabelSelect
              selectedLabels={selectedLabels}
              onChange={(values) => setSelectedLabels(values)}
            />
          </div>
        )}
      </div>
      <div className={classes.listBody}>
        {searchParam ? (
          <TicketsList
            searchParam={searchParam}
            showAll={true}
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            selectedLabels={selectedLabels}
          />
        ) : (
          <TicketsList
            status="open"
            showAll={true}
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            selectedLabels={listLabels}
            waiting={quickFilter === "waiting"}
          />
        )}
      </div>
    </Paper>
  );
};

export default TicketsManager;
